import { Menu, Search, Sparkles, Sun, Moon, User, LogOut } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { logout } from '@/auth/authClient';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useUiStore } from '@/store/uiStore';
import { useAskAiStore } from '@/store/askAiStore';
import { useTheme } from '@/hooks/useTheme';
import DetectiveTourButton from '@/tour/DetectiveTourButton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export default function TopBar() {
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const setCommandPaletteOpen = useUiStore((s) => s.setCommandPaletteOpen);
  const askAiOpen = useAskAiStore((s) => s.isOpen);
  const toggleAskAi = useAskAiStore((s) => s.toggle);
  const { theme, toggle } = useTheme();
  const user = useAuthStore((s) => s.user);
  const { pathname } = useLocation();
  // Map page has its own geocoder search bar floating over the canvas; hide
  // the topbar's command-palette search there to avoid two side-by-side
  // search inputs.
  const hideTopbarSearch = pathname === '/maps' || pathname.startsWith('/maps/');

  const displayName = user?.email ?? 'Account';

  async function onSignOut() {
    await logout();
    window.location.assign('/app/sign-in');
  }

  return (
    <header className="sticky top-0 z-40 flex h-14 items-center gap-2 border-b border-gray-200 bg-white/90 px-4 shadow-sm backdrop-blur-md supports-[backdrop-filter]:bg-white/75 dark:border-white/10 dark:bg-card/80 dark:supports-[backdrop-filter]:bg-card/70">
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleSidebar}
        aria-label="Toggle sidebar"
      >
        <Menu className="h-4 w-4" />
      </Button>

      {!hideTopbarSearch && (
        <Button
          variant="outline"
          className="ml-2 h-9 w-72 justify-start gap-2 text-muted-foreground"
          onClick={() => setCommandPaletteOpen(true)}
        >
          <Search className="h-4 w-4" />
          <span className="text-sm">Search assets, cards, merchants…</span>
          <kbd className="ml-auto rounded border bg-muted px-1.5 py-0.5 text-xs">⌘K</kbd>
        </Button>
      )}

      <div className="ml-auto flex items-center gap-2">
        <DetectiveTourButton />

        <Button
          variant={askAiOpen ? 'default' : 'outline'}
          size="sm"
          onClick={toggleAskAi}
        >
          <Sparkles className="mr-2 h-4 w-4" />
          Ask AI
        </Button>

        <Button variant="ghost" size="icon" onClick={toggle} aria-label="Toggle theme">
          {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="User menu">
              <User className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="truncate">{displayName}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled>Profile</DropdownMenuItem>
            <DropdownMenuItem disabled>Settings</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => void onSignOut()}>
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
